import moment from 'moment';
import { calculateCost } from '../utils/commonMethods';
import { CostingActivity } from './domainServices/ICostingActivity';
import { ICrewType } from './domainServices/ICrewType';
import { FireNumber } from './domainServices/IFireNumber';
import { RateType } from './domainServices/IRateType';
import { RateUnit } from './domainServices/IRateUnit';
import { IArrayOption } from './IArrayOption';

export interface IFlyingHours {
  flightReportCostDetailsId?: string;
  flightReportId?: number;

  fireNumberId?: any;
  fireNumber?: string;
  fireNumberEntity?: FireNumber | undefined;
  fireNumberList?: IArrayOption[];
  fireYear?: number;

  crewTypeId: any;
  crewTypeEntity?: ICrewType;
  crewTypeList?: IArrayOption[];

  costingActivityId: string | null;
  costingActivityCode?: string;
  costingActivityEntity?: CostingActivity | undefined;
  costingActivityList?: IArrayOption[];

  rateTypeId: string;
  rateTypeEntity?: RateType | undefined;
  rateTypeList?: IArrayOption[];

  noOfUnits: any;
  isNoOfUnitsError?: boolean;
  noOfUnitsError?: string;

  rateUnitId: string;
  rateUnitEntity?: RateUnit | undefined;
  rateUnitList?: IArrayOption[];

  ratePerUnit?: number;
  isRatePerUnitError?: boolean;
  ratePerUnitError?: string;

  cost?: any;

  internalOrder?: string | null;
  internalOrderList?: IArrayOption[];
  internalOrdersEnabled?: boolean;
  costCenter?: string;
  costCenterList?: IArrayOption[];
  fund?: string;
  fundList?: IArrayOption[];
  account?: string;
  accountList?: IArrayOption[];

  createUserId?: string;
  createTimestamp?: string;
  updateTimestamp?: string | null;
  updateUserId?: string | null;

  deleted?: boolean;
  expenseType?: string;

  isChecked?: boolean;

  invoiceId?: any;
  invoiceNumber?: string;
}

export function getFlyingHoursErrorMessages(flyingHours: IFlyingHours): string[] {
  const errors: string[] = [];
  if (flyingHours.ratePerUnitError) {
    errors.push(flyingHours.ratePerUnitError);
  }
  if (flyingHours.noOfUnitsError) {
    errors.push(flyingHours.noOfUnitsError);
  }

  return errors;
}
export class FlyingHours implements IFlyingHours {
  readonly flightReportCostDetailsId?: string;
  flightReportId?: number;

  readonly fireNumberId?: any;
  readonly fireNumber?: string;
  fireNumberEntity?: FireNumber;
  fireNumberList?: IArrayOption[];
  fireYear?: number;

  readonly crewTypeId: any;
  crewTypeEntity?: ICrewType;
  crewTypeList?: IArrayOption[];

  readonly costingActivityId: string;
  readonly costingActivityCode?: string;
  costingActivityEntity?: CostingActivity;
  costingActivityList?: IArrayOption[];
  internalOrdersEnabled?: boolean;

  rateTypeId: string;
  rateTypeEntity?: RateType;
  rateTypeList?: IArrayOption[];

  readonly noOfUnits: any;
  isNoOfUnitsError?: boolean;
  noOfUnitsError?: string;

  rateUnitId: string;
  rateUnitEntity?: RateUnit;
  rateUnitList?: IArrayOption[];

  ratePerUnit?: any;
  isRatePerUnitError?: boolean;
  ratePerUnitError?: string;
  readonly cost: any;

  readonly internalOrder?: string;
  internalOrderList?: IArrayOption[];
  readonly costCenter?: string;
  costCenterList?: IArrayOption[];
  fund?: string;
  fundList?: IArrayOption[];
  account?: string;
  accountList?: IArrayOption[];

  createUserId?: string;
  createTimestamp?: string;
  updateTimestamp?: string | null;
  updateUserId?: string | null;

  deleted?: boolean;
  expenseType?: string;

  isChecked?: boolean;

  invoiceId?: any;
  invoiceNumber?: string;

  constructor(from?: Partial<IFlyingHours>) {
    this.flightReportCostDetailsId = from?.flightReportCostDetailsId ?? '00000000-0000-0000-0000-000000000000';
    this.flightReportId = from?.flightReportId;

    this.fireNumberId = from?.fireNumberId;
    this.fireNumber = from?.fireNumber ?? '';
    this.fireNumberEntity = from?.fireNumberEntity ?? undefined;
    this.fireNumberList = from?.fireNumberList ?? undefined;
    this.fireYear = from?.fireYear;

    this.crewTypeId = from?.crewTypeId ?? null;
    this.crewTypeEntity = from?.crewTypeEntity ?? undefined;
    this.crewTypeList = from?.crewTypeList ?? undefined;

    this.costingActivityId = from?.costingActivityId ?? '00000000-0000-0000-0000-000000000000';
    this.costingActivityCode = from?.costingActivityCode ?? '';
    this.costingActivityEntity = from?.costingActivityEntity ?? undefined;
    this.costingActivityList = from?.costingActivityList ?? undefined;
    this.internalOrdersEnabled = from?.internalOrdersEnabled ?? true;

    this.rateTypeId = from?.rateTypeId ?? '';
    this.rateTypeEntity = from?.rateTypeEntity ?? undefined;
    this.rateTypeList = from?.rateTypeList ?? undefined;

    this.noOfUnits = from?.noOfUnits ?? 0;
    this.isNoOfUnitsError = from?.isNoOfUnitsError ?? false;
    this.noOfUnitsError = from?.noOfUnitsError ?? '';

    this.rateUnitId = from?.rateUnitId ?? '';
    this.rateUnitEntity = from?.rateUnitEntity ?? undefined;
    this.rateUnitList = from?.rateUnitList ?? undefined;

    this.ratePerUnit = from?.ratePerUnit ?? 0;
    this.isRatePerUnitError = from?.isRatePerUnitError ?? false;
    this.ratePerUnitError = from?.ratePerUnitError ?? '';

    this.cost = calculateCost(from?.noOfUnits, from?.ratePerUnit);

    this.internalOrder = from?.internalOrder ?? '';
    this.internalOrderList = from?.internalOrderList ?? undefined;
    this.costCenter = from?.costCenter ?? '';
    this.costCenterList = from?.costCenterList ?? undefined;
    this.fund = from?.fund ?? '';
    this.fundList = from?.fundList ?? undefined;
    this.account = from?.account ?? '';
    this.accountList = from?.accountList ?? undefined;

    this.createUserId = from?.createUserId ?? '';
    this.createTimestamp = this.dateFormat(from?.createTimestamp) ?? null;
    this.updateTimestamp = moment(from?.updateTimestamp).isValid() ? moment(from?.updateTimestamp).format('yyyy-MM-DD') : null;
    this.updateUserId = from?.updateUserId ?? null;

    this.deleted = from?.deleted ?? false;
    this.expenseType = from?.expenseType ?? '';

    this.isChecked = from?.isChecked ?? false;

    this.invoiceId = from?.invoiceId ?? null;
    this.invoiceNumber = from?.invoiceNumber ?? '';
  }

  dateFormat(value?: string): string {
    if (!value) return '';

    return moment(value).isValid() ? moment(value).format('yyyy-MM-DD') : value;
  }
}

// Internal
export interface IFlyingHoursExpenseInternal {
  flightReportCostDetailsId?: string;
  flightReportId?: number;

  fireNumberEntity?: FireNumber | undefined;
  fireNumberList?: IArrayOption[];

  crewTypeEntity?: ICrewType;
  crewTypeList?: IArrayOption[];

  costingActivityEntity?: CostingActivity | undefined;
  costingActivityList?: IArrayOption[];

  rateTypeEntity?: RateType | undefined;
  rateTypeList?: IArrayOption[];

  rateUnitEntity?: RateUnit | undefined;
  rateUnitList?: IArrayOption[];

  internalOrderList?: IArrayOption[];
  costCenterList?: IArrayOption[];
  fundList?: IArrayOption[];
  accountList?: IArrayOption[];
}

export class FlyingHoursExpenseInternal implements IFlyingHoursExpenseInternal {
  flightReportCostDetailsId?: string;
  flightReportId?: number;

  fireNumberEntity?: FireNumber | undefined;
  fireNumberList?: IArrayOption[];

  crewTypeEntity?: ICrewType;
  crewTypeList?: IArrayOption[];

  costingActivityEntity?: CostingActivity | undefined;
  costingActivityList?: IArrayOption[];

  rateTypeEntity?: RateType | undefined;
  rateTypeList?: IArrayOption[];

  rateUnitEntity?: RateUnit | undefined;
  rateUnitList?: IArrayOption[];

  internalOrderList?: IArrayOption[];
  costCenterList?: IArrayOption[];
  fundList?: IArrayOption[];
  accountList?: IArrayOption[];

  constructor(from?: Partial<IFlyingHoursExpenseInternal>) {
    this.flightReportCostDetailsId = from?.flightReportCostDetailsId ?? '00000000-0000-0000-0000-000000000000';
    this.flightReportId = from?.flightReportId;

    this.fireNumberEntity = from?.fireNumberEntity ?? undefined;
    this.fireNumberList = from?.fireNumberList ?? undefined;

    this.crewTypeEntity = from?.crewTypeEntity ?? undefined;
    this.crewTypeList = from?.crewTypeList ?? undefined;

    this.costingActivityEntity = from?.costingActivityEntity ?? undefined;
    this.costingActivityList = from?.costingActivityList ?? undefined;

    this.rateTypeEntity = from?.rateTypeEntity ?? undefined;
    this.rateTypeList = from?.rateTypeList ?? undefined;

    this.rateUnitEntity = from?.rateUnitEntity ?? undefined;
    this.rateUnitList = from?.rateUnitList ?? undefined;

    this.internalOrderList = from?.internalOrderList ?? undefined;
    this.costCenterList = from?.costCenterList ?? undefined;
    this.fundList = from?.fundList ?? undefined;
    this.accountList = from?.accountList ?? undefined;
  }
}
